/**
 * PIN gate for an employee's OWN salary page.
 *
 * Separate from `SalaryPassKeyContext`, which guards the admin payroll surfaces. That
 * one takes the shared Rate API key — `scope: all`, it reads every employee's rates —
 * so it can never be handed to staff. Here each employee sets six digits of their own.
 *
 * The PIN is never stored, here or on the server. The backend derives the employee's
 * Rate API passkey from it and asks that API whose key it is; the right PIN derives a
 * key it recognises, a wrong one derives a key it has never seen. So "wrong PIN" and
 * "no such key" are the same answer, and neither says which.
 *
 * Unlocking lasts four hours and survives reloads and other tabs; the screen re-locks
 * itself the moment it lapses rather than waiting for a call to fail.
 */

import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Lock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type { ApiError } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import {
  accountUnlockExpiresAt,
  clearAccountUnlock,
  hasAccountUnlock,
  setAccountUnlock,
} from '@/services/accountSalaryKey';
import { useAuth } from '@/hooks/useAuth';
import { AccountSalaryKeyContext } from '@/contexts/AccountSalaryKeyContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

const PIN_LENGTH = 6;

function errorMessage(e: unknown, fallback: string): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? (e instanceof Error ? e.message : fallback);
}

/** Strips anything that is not a digit and caps the length, so the field can only hold a PIN. */
function toPinDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, PIN_LENGTH);
}

export function AccountSalaryKeyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id;

  const [unlocked, setUnlocked] = useState(() => hasAccountUnlock(userId));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [busy, setBusy] = useState(false);

  // Follow the signed-in user: this is what re-locks the screen when someone logs out
  // and a different person signs in on the same machine.
  //
  // Adjusted during render rather than in an effect (React's documented pattern for
  // resetting state when an input changes). An effect would paint one frame showing the
  // previous person's unlocked state before correcting itself.
  const [unlockOwner, setUnlockOwner] = useState(userId);
  if (unlockOwner !== userId) {
    setUnlockOwner(userId);
    setUnlocked(hasAccountUnlock(userId));
  }

  // Whether this employee has set a PIN yet decides which half of the dialog to show.
  // Only asked while the dialog is open — a locked page does not need to know.
  const {
    data: keyStatus,
    isLoading: statusLoading,
    isError: statusFailed,
    error: statusError,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ['payroll', 'salary-key', 'me'],
    queryFn: () => payrollService.getMySalaryKeyStatus(),
    enabled: dialogOpen && !!userId,
  });
  const needsSetup = keyStatus?.exists === false;

  const requestUnlock = useCallback(() => {
    if (!hasAccountUnlock(userId)) setDialogOpen(true);
  }, [userId]);

  // Re-lock the moment the unlock lapses. Without this the screen stays open past its
  // window and only corrects itself on the next navigation.
  useEffect(() => {
    if (!unlocked) return;

    const expiresAt = accountUnlockExpiresAt(userId);
    if (!expiresAt) return;

    const timer = setTimeout(() => {
      clearAccountUnlock();
      setUnlocked(false);
    }, Math.max(0, expiresAt - Date.now()));

    return () => clearTimeout(timer);
  }, [unlocked, userId]);

  const closeDialog = () => {
    setDialogOpen(false);
    setPin('');
    setConfirmPin('');
  };

  const succeed = (key: string) => {
    if (!userId) return;
    setAccountUnlock(userId, key);
    setUnlocked(true);
    closeDialog();
    toast.success('Salary unlocked');
  };

  const handleSetPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== PIN_LENGTH) {
      toast.error(`PIN must be ${PIN_LENGTH} digits`);
      return;
    }
    if (pin !== confirmPin) {
      toast.error('Both PINs must match');
      return;
    }

    setBusy(true);
    try {
      await payrollService.setMySalaryPin(pin);
      // Unlock with the PIN just set, so the key comes back the same way it will on
      // every later visit — one path, not two.
      succeed(await payrollService.unlockMySalary(pin));
    } catch (err) {
      const ax = err as AxiosError<ApiError>;
      if (ax.response?.status === 409) {
        // A PIN was already set for this employee — the status query was stale. Re-read
        // it so the dialog switches to asking for the existing PIN instead.
        toast.error('You have already set a PIN — enter it instead');
        void refetchStatus();
        setPin('');
        setConfirmPin('');
      } else {
        toast.error(errorMessage(err, 'Could not set your PIN'));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== PIN_LENGTH) {
      toast.error(`PIN must be ${PIN_LENGTH} digits`);
      return;
    }

    setBusy(true);
    try {
      succeed(await payrollService.unlockMySalary(pin));
    } catch (err) {
      const ax = err as AxiosError<ApiError>;
      if (ax.response?.status === 401) {
        toast.error('Incorrect PIN');
        setPin('');
      } else {
        toast.error(errorMessage(err, 'Could not check your PIN'));
      }
    } finally {
      setBusy(false);
    }
  };

  const pinField = (
    value: string,
    onChange: (v: string) => void,
    placeholder: string,
    autoFocus = false,
  ) => (
    <div className="relative">
      <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        // Input carries `sm:px-3`, which an unprefixed `pl-9` does not merge away —
        // without `sm:pl-9` the text slides under the key icon from `sm` up.
        className="pl-9 sm:pl-9 tracking-[0.4em] placeholder:tracking-normal"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(toPinDigits(e.target.value))}
        autoFocus={autoFocus}
      />
    </div>
  );

  return (
    <AccountSalaryKeyContext.Provider value={{ unlocked, requestUnlock }}>
      {children}

      <Dialog open={dialogOpen} onOpenChange={(next) => !next && closeDialog()}>
        <DialogContent className="sm:max-w-sm">
          {statusLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : statusFailed ? (
            /* ── Status unknown: never guess "returning", or a first-timer is stuck ── */
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="rounded-full bg-destructive/10 p-3">
                <Lock className="h-6 w-6 text-destructive" />
              </div>
              <DialogTitle>Could not check your salary PIN</DialogTitle>
              <DialogDescription>
                {errorMessage(statusError, 'The salary service did not respond.')}
              </DialogDescription>
              <Button className="w-full" onClick={() => void refetchStatus()}>
                Retry
              </Button>
            </div>
          ) : needsSetup ? (
            /* ── First time: choose a PIN ── */
            <>
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="rounded-full bg-primary/10 p-3">
                  <ShieldCheck className="h-6 w-6 text-primary" />
                </div>
                <DialogTitle>Set your salary PIN</DialogTitle>
                <DialogDescription>
                  Choose {PIN_LENGTH} digits. You will need it each time you open your
                  salary details, so pick something you will remember.
                </DialogDescription>
              </div>

              <form onSubmit={(e) => void handleSetPin(e)} className="mt-2 space-y-3">
                {pinField(pin, setPin, `${PIN_LENGTH}-digit PIN`, true)}
                {pinField(confirmPin, setConfirmPin, 'Confirm PIN')}
                <Button type="submit" className="w-full" disabled={busy}>
                  {busy ? 'Saving…' : 'Set PIN & unlock'}
                </Button>
              </form>
            </>
          ) : (
            /* ── Returning: enter the PIN ── */
            <>
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="rounded-full bg-primary/10 p-3">
                  <Lock className="h-6 w-6 text-primary" />
                </div>
                <DialogTitle>Salary locked</DialogTitle>
                <DialogDescription>
                  Enter your {PIN_LENGTH}-digit salary PIN to view your pay details.
                </DialogDescription>
              </div>

              <form onSubmit={(e) => void handleUnlock(e)} className="mt-2 space-y-3">
                {pinField(pin, setPin, `${PIN_LENGTH}-digit PIN`, true)}
                <Button type="submit" className="w-full" disabled={busy}>
                  {busy ? 'Checking…' : 'Unlock'}
                </Button>
              </form>

              <p className="mt-1 text-center text-xs text-muted-foreground">
                Forgotten your PIN? Ask an administrator to reset it.
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AccountSalaryKeyContext.Provider>
  );
}
